import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtCompact, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageBody } from "@/components/common/PageHeader";
import OnboardingChecklist from "@/components/common/OnboardingChecklist";
import { Button } from "@/components/ui/button";
import { Plus, ArrowUpRight, Sparkles } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell } from "recharts";

/* ------------- Reusable terminal primitives ------------- */

function Pane({ children, className = "", testid }) {
    return (
        <div
            data-testid={testid}
            className={`border border-border bg-card relative ${className}`}
            style={{ borderRadius: "2px" }}
        >
            {children}
        </div>
    );
}

function PaneTitle({ kicker, children, right }) {
    return (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <div className="flex items-center gap-2">
                <span className="live-dot" style={{ background: "hsl(var(--gold))", boxShadow: "0 0 6px hsl(var(--gold) / 0.6)" }} />
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{kicker}</span>
                {children && <span className="text-[11px] text-foreground/80 font-mono">{children}</span>}
            </div>
            {right}
        </div>
    );
}

function HeroNumber({ kicker, value, suffix, ccy, sub, route, navigate, big = false }) {
    return (
        <button
            onClick={() => route && navigate(route)}
            className="w-full text-left p-4 hover:bg-gold/5 transition-colors group flex flex-col"
            data-testid={`hero-${kicker.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">{kicker}</div>
            <div className={`font-display tracking-tight ${big ? "text-[44px] leading-[1.05]" : "text-[24px] leading-tight"}`}>
                {value == null ? "—" : fmtCompact(value, ccy)}
                {suffix && <span className="text-muted-foreground text-[14px] ml-1.5 font-sans">{suffix}</span>}
            </div>
            {sub && <div className="text-[11px] text-muted-foreground font-mono mt-1.5 tracking-[0.04em]">{sub}</div>}
            {route && <div className="text-[10px] uppercase tracking-[0.16em] text-gold opacity-0 group-hover:opacity-100 transition-opacity mt-1.5">Open →</div>}
        </button>
    );
}

function TickerCell({ label, value, ccy, route, navigate, tone = "default" }) {
    const toneClass =
        tone === "positive" ? "text-positive" :
        tone === "destructive" ? "text-destructive" :
        tone === "warning" ? "text-gold" :
        "text-foreground";
    return (
        <button
            onClick={() => navigate(route)}
            className="text-left p-3 border-r border-border last:border-r-0 hover:bg-gold/5 flex flex-col gap-1 transition-colors"
            data-testid={`ticker-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <span className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
            <span className={`font-mono tabular-nums text-[14px] font-medium ${toneClass}`}>
                {value == null ? "—" : fmtCompact(value, ccy)}
            </span>
        </button>
    );
}

/* ------------- Page ------------- */

export default function Dashboard() {
    const { activeOrg } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cashflow, setCashflow] = useState(null);
    const [cfPeriod, setCfPeriod] = useState("this_fy");
    const [ie, setIe] = useState(null);
    const [iePeriod, setIePeriod] = useState("this_fy");
    const [availableFys, setAvailableFys] = useState([]);
    const [fyYear, setFyYear] = useState(null); // null = current FY
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/dashboard/available-fys").then(({ data }) => {
            setAvailableFys(data.fys || []);
            if (fyYear == null) setFyYear(data.current_fy);
        }).catch(() => {});
    }, [activeOrg?.id]);

    useEffect(() => {
        const params = fyYear ? { fy_year: fyYear } : {};
        api.get("/dashboard", { params }).then(({ data }) => { setData(data); setLoading(false); });
    }, [activeOrg?.id, fyYear]);

    // If user picks a non-current FY, the pane period becomes that specific FY (fy_YYYY);
    // otherwise the existing FY/PFY/12M toggle inside the pane is used verbatim.
    const currentFy = availableFys.find((f) => f.is_current)?.year;
    const isCustomFy = fyYear && currentFy && fyYear !== currentFy;
    const effectiveCfPeriod = isCustomFy ? `fy_${fyYear}` : cfPeriod;
    const effectiveIePeriod = isCustomFy ? `fy_${fyYear}` : iePeriod;

    useEffect(() => {
        api.get(`/dashboard/cashflow`, { params: { period: effectiveCfPeriod } })
            .then(({ data }) => setCashflow(data));
    }, [effectiveCfPeriod, activeOrg?.id]);

    useEffect(() => {
        api.get(`/dashboard/income-expense`, { params: { period: effectiveIePeriod, basis: "accrual" } })
            .then(({ data }) => setIe(data));
    }, [effectiveIePeriod, activeOrg?.id]);

    const ccy = activeOrg?.base_currency || "INR";
    const k = data?.kpis || {};
    const todayStr = new Date().toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

    // Selected FY info for label
    const selectedFy = availableFys.find((f) => f.year === fyYear) || availableFys[0];
    const fyRangeLabel = selectedFy ? (() => {
        const s = new Date(selectedFy.starts_at);
        const e = new Date(selectedFy.ends_at);
        const fmt = (d) => d.toLocaleDateString("en-IN", { month: "short", year: "numeric" }).toUpperCase();
        return `${fmt(s)} – ${fmt(e)}${selectedFy.is_current ? " · YTD" : ""}`;
    })() : "";

    return (
        <>
            {/* Editorial page header */}
            <div className="px-6 pt-6 pb-4 bg-background flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-1.5 flex-wrap">
                        <span>{todayStr}</span>
                        <span className="opacity-40">·</span>
                        <span className="text-gold">FYTD COMMAND CENTER</span>
                        <span className="live-dot" />
                        {selectedFy && (
                            <>
                                <span className="opacity-40">·</span>
                                <span className="text-foreground font-medium" data-testid="dash-fy-label">
                                    {selectedFy.label}
                                </span>
                                <span className="text-muted-foreground/70" data-testid="dash-fy-range">
                                    {fyRangeLabel}
                                </span>
                            </>
                        )}
                    </div>
                    <h1 className="font-display text-[44px] leading-[1.02] tracking-tight font-light text-foreground">
                        <span className="italic" style={{ color: "hsl(var(--gold))" }}>{activeOrg?.name || "Your books"}</span>
                    </h1>
                    <div className="text-[12px] text-muted-foreground mt-2 font-mono opacity-80">
                        $ vyaahar status --fy {selectedFy ? selectedFy.id : "current"} --basis accrual
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {availableFys.length > 0 && (
                        <select
                            value={fyYear ?? ""}
                            onChange={(e) => setFyYear(Number(e.target.value))}
                            data-testid="dash-fy-select"
                            className="h-9 rounded-md border border-border bg-card px-3 text-[12px] font-mono tracking-[0.06em] focus:outline-none focus:ring-1 focus:ring-gold cursor-pointer"
                        >
                            {availableFys.map((f) => (
                                <option key={f.id} value={f.year} data-testid={`dash-fy-option-${f.year}`}>
                                    {f.label}{f.is_current ? " · current" : ""}
                                </option>
                            ))}
                        </select>
                    )}
                    <Button variant="outline" size="sm" onClick={() => navigate("/reports")} data-testid="dash-view-reports">Reports</Button>
                    <Button size="sm" onClick={() => navigate("/sales/invoices/new")} data-testid="dash-new-invoice">
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> New invoice
                    </Button>
                </div>
            </div>

            <PageBody>
                <OnboardingChecklist />

                {loading ? (
                    <div className="grid grid-cols-12 gap-px bg-border" style={{ minHeight: 320 }}>
                        {[3,6,3].map((c, i) => <div key={i} className={`col-span-${c} bg-card shimmer`} />)}
                    </div>
                ) : (
                    <>
                        {/* ======= HERO BAND — dark navy island on light canvas ======= */}
                        <div className="dark-pane grid grid-cols-12 gap-px border" style={{ borderRadius: "2px", background: "hsl(var(--dark-pane-border))" }}>
                            {/* LEFT: live ticker tape */}
                            <div className="dark-pane col-span-12 md:col-span-3 flex flex-col" data-pane="card">
                                <div className="px-4 py-2.5 border-b flex items-center gap-2" data-pane="border">
                                    <span className="live-dot" />
                                    <span className="text-[10px] uppercase tracking-[0.18em] font-medium" data-pane="muted">Activity tape</span>
                                </div>
                                <div className="flex-1 overflow-y-auto" style={{ maxHeight: 360 }}>
                                    {(data?.recent_activity || []).slice(0, 12).map((a, i) => (
                                        <div key={i} className="px-4 py-2 border-b text-[11.5px]" data-pane="border" data-testid={`tape-${i}`}>
                                            <div className="flex items-baseline justify-between gap-2">
                                                <span className="font-medium capitalize truncate" style={{ color: "hsl(var(--dark-pane-fg))" }}>{a.action}</span>
                                                <span className="font-mono shrink-0 tabular-nums" data-pane="muted" style={{ fontSize: 9.5 }}>
                                                    {new Date(a.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })}
                                                </span>
                                            </div>
                                            <div className="text-[10.5px] truncate font-mono" data-pane="muted">
                                                {a.entity}{a.details?.number ? `:${a.details.number}` : ""}
                                            </div>
                                        </div>
                                    ))}
                                    {(!data?.recent_activity || data.recent_activity.length === 0) && (
                                        <div className="px-4 py-6 text-[11px] font-mono" data-pane="muted">{`> AWAITING DATA`}<span className="term-cursor" /></div>
                                    )}
                                </div>
                            </div>

                            {/* CENTER: massive hero number + sparkline */}
                            <div className="dark-pane col-span-12 md:col-span-6 flex flex-col" data-pane="card">
                                <div className="px-4 py-2.5 border-b flex items-center justify-between" data-pane="border">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-[0.18em] font-medium" data-pane="muted">
                                            Net profit · {selectedFy?.label || "FYTD"}
                                        </span>
                                    </div>
                                    <span className="text-[10px] font-mono tabular-nums" data-pane="muted">{selectedFy?.is_current ? todayStr : fyRangeLabel}</span>
                                </div>
                                <div className="px-6 py-7 flex-1 flex flex-col justify-center">
                                    <div className="font-display tracking-tight" style={{
                                        fontSize: 72, lineHeight: 1, fontWeight: 300,
                                        color: (k.net_profit || 0) >= 0 ? "hsl(var(--dark-pane-fg))" : "hsl(var(--destructive))",
                                    }} data-testid="hero-net-profit">
                                        <span className="italic text-[28px] mr-2 font-display" data-pane="muted">₹</span>
                                        {fmtCompact(k.net_profit, ccy).replace(/[₹$€£]/, "")}
                                    </div>
                                    <div className="flex items-baseline gap-5 mt-3 text-[12px] font-mono tabular-nums">
                                        <span data-pane="muted">REV <span style={{ color: "hsl(var(--dark-pane-fg))" }}>{fmtCompact(k.revenue_fy, ccy)}</span></span>
                                        <span data-pane="muted">EXP <span style={{ color: "hsl(var(--destructive))" }}>{fmtCompact(k.expenses_fy, ccy)}</span></span>
                                        <span data-pane="muted">COLL <span style={{ color: "hsl(var(--positive))" }}>{fmtCompact(k.collections_fy, ccy)}</span></span>
                                    </div>
                                    <div className="mt-4 -mx-2">
                                        <ResponsiveContainer width="100%" height={70}>
                                            <LineChart data={cashflow?.months || []} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                                                <XAxis dataKey="month_short" hide />
                                                <YAxis hide />
                                                <Line type="monotone" dataKey="balance" stroke="hsl(var(--gold))" strokeWidth={1.8} dot={false}
                                                    activeDot={{ r: 4, fill: "hsl(var(--gold))", stroke: "hsl(var(--card))", strokeWidth: 1.5 }} />
                                                <Tooltip
                                                    formatter={(v) => [fmtMoney(v, ccy), "Balance"]}
                                                    labelFormatter={(l) => l}
                                                    cursor={{ stroke: "hsl(var(--gold) / 0.4)", strokeDasharray: "2 3" }}
                                                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--gold) / 0.5)", borderRadius: 2, fontSize: 11, padding: "6px 10px", fontFamily: "IBM Plex Mono" }}
                                                    labelStyle={{ fontSize: 9.5, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}
                                                    wrapperStyle={{ zIndex: 20, outline: "none" }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT: AI copilot terminal */}
                            <div className="dark-pane col-span-12 md:col-span-3 flex flex-col" data-pane="card">
                                <div className="px-4 py-2.5 border-b flex items-center gap-2" data-pane="border">
                                    <Sparkles className="w-3 h-3 text-gold" />
                                    <span className="text-[10px] uppercase tracking-[0.18em] font-medium" data-pane="muted">AI Copilot</span>
                                </div>
                                <div className="p-4 flex-1 flex flex-col">
                                    <div className="font-mono text-[12px] leading-relaxed" style={{ color: "hsl(var(--dark-pane-fg))" }}>
                                        <div className="text-gold">{"> vyaahar.ai"}</div>
                                        <div className="mt-1">Ready. Ask anything about your books.</div>
                                    </div>
                                    <div className="mt-4 space-y-1.5">
                                        {["Summarize this month", "Who's most overdue?", "Why is profit lower?"].map((s) => (
                                            <button
                                                key={s}
                                                className="w-full text-left text-[11.5px] font-mono px-2.5 py-1.5 border bg-transparent hover:border-gold transition-colors"
                                                onClick={() => navigate("/copilot")}
                                                data-testid={`dash-suggest-${s.slice(0, 12)}`}
                                                style={{ borderRadius: 2, borderColor: "hsl(var(--dark-pane-border))", color: "hsl(var(--dark-pane-fg))" }}
                                            >
                                                {`> ${s}`}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => navigate("/copilot")}
                                        data-testid="dash-open-copilot"
                                        className="mt-auto text-[11.5px] uppercase tracking-[0.16em] text-gold hover:underline self-start mt-4"
                                    >
                                        Open Copilot Terminal →
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* ======= TICKER ROW — secondary KPIs as a flat strip ======= */}
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-8 bg-card border border-border" style={{ borderRadius: "2px" }}>
                            <TickerCell label="Revenue FYTD"    value={k.revenue_fy}     ccy={ccy} navigate={navigate} route="/reports/profit-loss" tone="positive" />
                            <TickerCell label="Collections"     value={k.collections_fy} ccy={ccy} navigate={navigate} route="/sales/payments-received" tone="positive" />
                            <TickerCell label="Expenses FYTD"   value={k.expenses_fy}    ccy={ccy} navigate={navigate} route="/purchases/bills"   tone="destructive" />
                            <TickerCell label="Receivables"     value={k.receivables}    ccy={ccy} navigate={navigate} route="/reports/ar-aging"   tone="warning" />
                            <TickerCell label="Payables"        value={k.payables}       ccy={ccy} navigate={navigate} route="/reports/ap-aging" />
                            <TickerCell label="Cash & Bank"     value={k.cash}           ccy={ccy} navigate={navigate} route="/banking/accounts"   tone="positive" />
                            <TickerCell label="GST Liability"   value={k.gst_liability}  ccy={ccy} navigate={navigate} route="/reports/gst-summary" tone={k.gst_liability > 0 ? "warning" : "positive"} />
                            <TickerCell label="Net Profit"      value={k.net_profit}     ccy={ccy} navigate={navigate} route="/reports/profit-loss" tone={k.net_profit >= 0 ? "positive" : "destructive"} />
                        </div>

                        {/* ======= CASH FLOW + INCOME/EXPENSE ======= */}
                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <Pane className="col-span-12 lg:col-span-8" testid="cashflow-pane">
                                <PaneTitle
                                    kicker="Cash flow"
                                    right={
                                        <div className="flex items-center gap-px bg-border" style={{ borderRadius: 2 }} data-testid="cashflow-period-filter">
                                            {[{v:"this_fy",l:"FY"},{v:"prev_fy",l:"PFY"},{v:"last_12m",l:"12M"}].map((opt) => (
                                                <button key={opt.v} onClick={() => setCfPeriod(opt.v)} data-testid={`cashflow-${opt.v}`}
                                                    className={`px-2 py-1 text-[10px] font-mono tracking-[0.12em] uppercase ${cfPeriod===opt.v ? "bg-gold text-background" : "bg-card text-muted-foreground hover:text-foreground"}`}>
                                                    {opt.l}
                                                </button>
                                            ))}
                                        </div>
                                    }
                                >
                                    {cashflow ? `closing ${fmtCompact(cashflow.closing_cash, ccy)}` : ""}
                                </PaneTitle>
                                <div className="p-4">
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart data={cashflow?.months || []} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                                            <defs>
                                                <linearGradient id="cfBalance" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="hsl(var(--gold))" stopOpacity={0.35} />
                                                    <stop offset="100%" stopColor="hsl(var(--gold))" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} opacity={0.5} />
                                            <XAxis dataKey="month_short" tickLine={false} axisLine={false}
                                                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} dy={6}
                                                interval={0} padding={{ left: 10, right: 10 }} />
                                            <YAxis tickFormatter={(v) => fmtCompact(v, ccy)} tickLine={false} axisLine={false}
                                                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} width={56} />
                                            <Tooltip
                                                formatter={(v) => fmtMoney(v, ccy)}
                                                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--gold) / 0.5)", borderRadius: 2, fontSize: 11, padding: "6px 10px", fontFamily: "IBM Plex Mono" }}
                                                labelStyle={{ fontSize: 9.5, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}
                                            />
                                            <Area type="monotone" dataKey="balance" stroke="hsl(var(--gold))" strokeWidth={1.8}
                                                fill="url(#cfBalance)" dot={false}
                                                activeDot={{ r: 4, fill: "hsl(var(--gold))", stroke: "hsl(var(--background))", strokeWidth: 1.5 }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </Pane>

                            <Pane className="col-span-12 lg:col-span-4" testid="ie-pane">
                                <PaneTitle
                                    kicker="Income / Expense"
                                    right={
                                        <div className="flex items-center gap-px bg-border" style={{ borderRadius: 2 }} data-testid="ie-period-filter">
                                            {[{v:"this_fy",l:"FY"},{v:"prev_fy",l:"PFY"},{v:"last_12m",l:"12M"}].map((opt) => (
                                                <button key={opt.v} onClick={() => setIePeriod(opt.v)} data-testid={`ie-period-${opt.v}`}
                                                    className={`px-2 py-1 text-[10px] font-mono tracking-[0.12em] uppercase ${iePeriod===opt.v ? "bg-gold text-background" : "bg-card text-muted-foreground hover:text-foreground"}`}>
                                                    {opt.l}
                                                </button>
                                            ))}
                                        </div>
                                    }
                                />
                                <div className="p-4">
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <div>
                                            <div className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">Income</div>
                                            <div className="font-mono tabular-nums text-[18px] font-medium text-positive">{ie ? fmtCompact(ie.total_income, ccy) : "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">Expense</div>
                                            <div className="font-mono tabular-nums text-[18px] font-medium text-destructive">{ie ? fmtCompact(ie.total_expense, ccy) : "—"}</div>
                                        </div>
                                    </div>
                                    <ResponsiveContainer width="100%" height={170}>
                                        <BarChart data={ie?.months || []} margin={{ top: 8, right: 0, bottom: 0, left: 0 }} barCategoryGap="22%">
                                            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} opacity={0.5} />
                                            <XAxis dataKey="month_short" tickLine={false} axisLine={false}
                                                tick={{ fontSize: 9.5, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} dy={6}
                                                interval={0} padding={{ left: 4, right: 4 }} />
                                            <YAxis tickFormatter={(v) => fmtCompact(v, ccy)} tickLine={false} axisLine={false}
                                                tick={{ fontSize: 9.5, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} width={44} />
                                            <Tooltip formatter={(v) => fmtMoney(v, ccy)}
                                                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--gold) / 0.5)", borderRadius: 2, fontSize: 11, padding: "6px 10px", fontFamily: "IBM Plex Mono" }} />
                                            <Bar dataKey="income" fill="hsl(var(--positive))" radius={[0,0,0,0]} />
                                            <Bar dataKey="expense" fill="hsl(var(--destructive))" radius={[0,0,0,0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Pane>
                        </div>

                        {/* ======= AGING + TOP CUSTOMERS / VENDORS ======= */}
                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <Pane className="col-span-12 lg:col-span-4" testid="aging-pane">
                                <PaneTitle kicker="AR Aging" />
                                <div className="p-4">
                                    <ResponsiveContainer width="100%" height={170}>
                                        <BarChart data={[
                                            { b: "Current", v: data?.aging?.current || 0 },
                                            { b: "1–30",   v: data?.aging?.d1_30   || 0 },
                                            { b: "31–60",  v: data?.aging?.d31_60  || 0 },
                                            { b: "61–90",  v: data?.aging?.d61_90  || 0 },
                                            { b: "90+",    v: data?.aging?.d90_plus|| 0 },
                                        ]} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                                            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} opacity={0.5} />
                                            <XAxis dataKey="b" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} />
                                            <YAxis tickFormatter={(v) => fmtCompact(v, ccy)} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", fontFamily: "IBM Plex Mono" }} width={46} />
                                            <Tooltip formatter={(v) => fmtCompact(v, ccy)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--gold) / 0.5)", borderRadius: 2, fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                            <Bar dataKey="v" fill="hsl(var(--gold))" radius={[0,0,0,0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Pane>
                            <Pane className="col-span-12 lg:col-span-4" testid="top-cust-pane">
                                <PaneTitle kicker="Top Customers" />
                                <div className="p-4">
                                    <RankList items={data?.top_customers || []} ccy={ccy} accent="hsl(var(--positive))" testIdPrefix="top-cust" />
                                </div>
                            </Pane>
                            <Pane className="col-span-12 lg:col-span-4" testid="top-vend-pane">
                                <PaneTitle kicker="Top Vendors" />
                                <div className="p-4">
                                    <RankList items={data?.top_vendors || []} ccy={ccy} accent="hsl(var(--data-blue))" testIdPrefix="top-vend" />
                                </div>
                            </Pane>
                        </div>

                        {/* ======= OVERDUE + UPCOMING TABLES ======= */}
                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <Pane className="col-span-12 lg:col-span-6" testid="overdue-pane">
                                <PaneTitle kicker="Overdue Invoices" right={
                                    <Button variant="ghost" size="sm" onClick={() => navigate("/sales/invoices?status=overdue")} className="h-7 text-[11px]">
                                        All <ArrowUpRight className="w-3 h-3 ml-1" />
                                    </Button>
                                } />
                                <table className="fin-table">
                                    <thead><tr><th>INV</th><th>CUSTOMER</th><th className="num">AMOUNT</th><th className="num">DAYS</th></tr></thead>
                                    <tbody>
                                        {(data?.overdue_invoices || []).slice(0, 6).map((o) => (
                                            <tr key={o.id} onClick={() => navigate(`/sales/invoices/${o.id}`)} className="cursor-pointer" data-testid={`overdue-${o.id}`}>
                                                <td className="font-mono text-[12px]">{o.number}</td>
                                                <td className="truncate max-w-[200px]">{o.customer}</td>
                                                <td className="num">{fmtMoney(o.amount, o.currency)}</td>
                                                <td className="num"><span className="text-destructive">{o.days_overdue}d</span></td>
                                            </tr>
                                        ))}
                                        {(!data?.overdue_invoices || data?.overdue_invoices.length === 0) && (
                                            <tr><td colSpan={4} className="text-center py-6 text-muted-foreground text-[11.5px] font-mono">{`> ALL CURRENT`}<span className="term-cursor" /></td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </Pane>
                            <Pane className="col-span-12 lg:col-span-6" testid="upcoming-pane">
                                <PaneTitle kicker="Upcoming Bills · Next 14d" right={
                                    <Button variant="ghost" size="sm" onClick={() => navigate("/purchases/bills")} className="h-7 text-[11px]">
                                        All <ArrowUpRight className="w-3 h-3 ml-1" />
                                    </Button>
                                } />
                                <table className="fin-table">
                                    <thead><tr><th>BILL</th><th>VENDOR</th><th className="num">AMOUNT</th><th>DUE</th></tr></thead>
                                    <tbody>
                                        {(data?.upcoming_bills || []).slice(0, 6).map((b) => (
                                            <tr key={b.id}>
                                                <td className="font-mono text-[12px]">{b.number}</td>
                                                <td className="truncate max-w-[200px]">{b.vendor}</td>
                                                <td className="num">{fmtMoney(b.amount, ccy)}</td>
                                                <td className="text-[11.5px] font-mono text-muted-foreground">{fmtDate(b.due_date)}</td>
                                            </tr>
                                        ))}
                                        {(!data?.upcoming_bills || data?.upcoming_bills.length === 0) && (
                                            <tr><td colSpan={4} className="text-center py-6 text-muted-foreground text-[11.5px] font-mono">{`> NO BILLS DUE`}<span className="term-cursor" /></td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </Pane>
                        </div>

                        {/* ======= TOP EXPENSES DONUT (full width band) ======= */}
                        {ie?.top_expenses?.length > 0 && (
                            <Pane className="mt-4" testid="top-expenses-pane">
                                <PaneTitle kicker="Top Expenses · Where money goes" />
                                <div className="p-5">
                                    <TopExpensesDonut data={ie.top_expenses} ccy={ccy} />
                                </div>
                            </Pane>
                        )}
                    </>
                )}
            </PageBody>
        </>
    );
}

function RankList({ items, ccy, accent, testIdPrefix }) {
    const max = Math.max(...items.map((i) => i.amount || 0), 1);
    if (!items.length) {
        return <div className="text-[11.5px] text-muted-foreground py-6 text-center font-mono">{`> NO DATA`}</div>;
    }
    return (
        <div className="space-y-3">
            {items.slice(0, 6).map((c, idx) => {
                const pct = Math.max(2, Math.round(((c.amount || 0) / max) * 100));
                return (
                    <div key={c.id || idx} data-testid={`${testIdPrefix}-${c.id || idx}`}>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="text-[10px] font-mono text-muted-foreground tabular-nums w-5">{String(idx + 1).padStart(2, "0")}</span>
                                <span className="text-[12.5px] font-medium truncate">{c.name}</span>
                            </div>
                            <span className="font-mono tabular-nums text-[12px] shrink-0">{fmtCompact(c.amount, ccy)}</span>
                        </div>
                        <div className="h-px bg-border overflow-hidden">
                            <div className="h-full" style={{ width: `${pct}%`, background: accent }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

const DONUT_COLORS = [
    "hsl(var(--gold))",
    "hsl(var(--teal))",
    "hsl(var(--positive))",
    "hsl(var(--data-blue))",
    "hsl(var(--violet))",
    "hsl(var(--muted-foreground))",
];

function TopExpensesDonut({ data, ccy }) {
    const total = data.reduce((s, d) => s + d.amount, 0);
    return (
        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 items-center">
            <div className="relative mx-auto" style={{ width: 180, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <Pie data={data} dataKey="amount" nameKey="name" innerRadius={58} outerRadius={82}
                            paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                            {data.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                        </Pie>
                        <Tooltip
                            formatter={(v, _n, item) => [fmtMoney(v, ccy), item?.payload?.name]}
                            separator=" · "
                            contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--gold) / 0.5)", borderRadius: 2, fontSize: 11, padding: "6px 10px", fontFamily: "IBM Plex Mono" }}
                            wrapperStyle={{ zIndex: 30, outline: "none", pointerEvents: "none" }}
                            allowEscapeViewBox={{ x: true, y: true }}
                            position={{ y: -46 }}
                        />
                    </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Total</span>
                    <span className="font-mono tabular-nums text-[14px] font-semibold">{fmtCompact(total, ccy)}</span>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 min-w-0">
                {data.slice(0, 8).map((d, i) => (
                    <div key={d.name} className="flex items-center justify-between gap-2 text-[12px]" data-testid={`top-exp-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                            <span className="truncate">{d.name}</span>
                        </div>
                        <span className="font-mono tabular-nums shrink-0 text-[11.5px]">{fmtCompact(d.amount, ccy)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
